// Encode a stem (WAV blob) to a playable Ogg/Opus blob using the browser's
// native WebCodecs Opus encoder + a tiny hand-rolled Ogg muxer (no dependency).
// Opus runs at 48 kHz, so the 44.1 kHz stems are resampled first. The result is
// a standard .opus file the <audio> engine plays directly on re-open — ~7×
// smaller than the WAV, with practically inaudible loss at 192 kbps.
//
// wavToOpus() returns null on any failure (unsupported codec, encode error) so
// the caller transparently falls back to caching the original WAV.

const BITRATE = 192000;

export async function opusSupported(): Promise<boolean> {
  try {
    if (typeof AudioEncoder === 'undefined') return false;
    const { supported } = await AudioEncoder.isConfigSupported({
      codec: 'opus',
      sampleRate: 48000,
      numberOfChannels: 2,
      bitrate: BITRATE,
    });
    return !!supported;
  } catch {
    return false;
  }
}

export async function wavToOpus(wav: Blob): Promise<Blob | null> {
  try {
    if (typeof AudioEncoder === 'undefined' || typeof AudioData === 'undefined') return null;
    const buf = await decodeResampled(wav);
    if (!buf) return null;
    return await encodeOggOpus(buf);
  } catch {
    return null;
  }
}

// ── decode + resample to 48 kHz ──────────────────────────────────────────
async function decodeResampled(wav: Blob): Promise<AudioBuffer | null> {
  const ab = await wav.arrayBuffer();
  const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const ctx = new AC();
  let decoded: AudioBuffer;
  try {
    decoded = await ctx.decodeAudioData(ab);
  } finally {
    try {
      ctx.close();
    } catch {
      /* ignore */
    }
  }
  if (decoded.sampleRate === 48000) return decoded;
  const off = new OfflineAudioContext(decoded.numberOfChannels, Math.ceil(decoded.duration * 48000), 48000);
  const src = off.createBufferSource();
  src.buffer = decoded;
  src.connect(off.destination);
  src.start();
  return await off.startRendering();
}

// ── encode + mux ─────────────────────────────────────────────────────────
interface OpusPacket {
  data: Uint8Array;
  samples: number; // 48 kHz samples this packet decodes to
}

async function encodeOggOpus(buf: AudioBuffer): Promise<Blob | null> {
  const channels = buf.numberOfChannels;
  const frames = buf.length;
  const sr = 48000;
  const cfg: AudioEncoderConfig = { codec: 'opus', sampleRate: sr, numberOfChannels: channels, bitrate: BITRATE };
  const { supported } = await AudioEncoder.isConfigSupported(cfg);
  if (!supported) return null;

  const packets: OpusPacket[] = [];
  let head: Uint8Array | null = null;
  let encErr: unknown = null;

  const encoder = new AudioEncoder({
    output: (chunk, metadata) => {
      const desc = metadata?.decoderConfig?.description;
      if (desc && !head) head = new Uint8Array(desc as ArrayBuffer);
      const d = new Uint8Array(chunk.byteLength);
      chunk.copyTo(d);
      packets.push({ data: d, samples: Math.round(((chunk.duration ?? 0) * sr) / 1e6) });
    },
    error: (e) => {
      encErr = e;
    },
  });
  encoder.configure(cfg);

  // Planar copy of the whole buffer, then feed in 0.5 s blocks.
  const planar = new Float32Array(frames * channels);
  for (let c = 0; c < channels; c++) buf.copyFromChannel(planar.subarray(c * frames, (c + 1) * frames), c);

  const BLOCK = sr / 2;
  for (let off = 0; off < frames; off += BLOCK) {
    const n = Math.min(BLOCK, frames - off);
    const slice = new Float32Array(n * channels);
    for (let c = 0; c < channels; c++) slice.set(planar.subarray(c * frames + off, c * frames + off + n), c * n);
    const ad = new AudioData({
      format: 'f32-planar',
      sampleRate: sr,
      numberOfFrames: n,
      numberOfChannels: channels,
      timestamp: Math.round((off / sr) * 1e6),
      data: slice,
    });
    encoder.encode(ad);
    ad.close();
  }
  await encoder.flush();
  encoder.close();

  if (encErr || !head || packets.length === 0) return null;
  return muxOggOpus(head, packets);
}

// ── Ogg container (CRC + page framing) ───────────────────────────────────
const OGG_CRC = (() => {
  const t = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let r = i << 24;
    for (let j = 0; j < 8; j++) r = (r & 0x80000000 ? (r << 1) ^ 0x04c11db7 : r << 1) >>> 0;
    t[i] = r >>> 0;
  }
  return t;
})();

function oggCrc(data: Uint8Array): number {
  let crc = 0;
  for (let i = 0; i < data.length; i++) crc = ((crc << 8) ^ OGG_CRC[((crc >>> 24) ^ data[i]) & 0xff]) >>> 0;
  return crc >>> 0;
}

function lacing(lengths: number[]): Uint8Array {
  const segs: number[] = [];
  for (const len of lengths) {
    let l = len;
    while (l >= 255) {
      segs.push(255);
      l -= 255;
    }
    segs.push(l);
  }
  return new Uint8Array(segs);
}

function buildPage(headerType: number, granule: number, serial: number, seq: number, segTable: Uint8Array, payload: Uint8Array): Uint8Array {
  const page = new Uint8Array(27 + segTable.length + payload.length);
  const dv = new DataView(page.buffer);
  page[0] = 0x4f; // O
  page[1] = 0x67; // g
  page[2] = 0x67; // g
  page[3] = 0x53; // S
  page[4] = 0; // version
  page[5] = headerType;
  dv.setUint32(6, granule >>> 0, true);
  dv.setUint32(10, Math.floor(granule / 4294967296) >>> 0, true);
  dv.setUint32(14, serial >>> 0, true);
  dv.setUint32(18, seq >>> 0, true);
  dv.setUint32(22, 0, true); // CRC placeholder
  page[26] = segTable.length;
  page.set(segTable, 27);
  page.set(payload, 27 + segTable.length);
  dv.setUint32(22, oggCrc(page), true);
  return page;
}

function buildOpusTags(): Uint8Array {
  const vendor = new TextEncoder().encode('riffloop');
  const out = new Uint8Array(8 + 4 + vendor.length + 4);
  out.set(new TextEncoder().encode('OpusTags'), 0);
  const dv = new DataView(out.buffer);
  dv.setUint32(8, vendor.length, true);
  out.set(vendor, 12);
  dv.setUint32(12 + vendor.length, 0, true);
  return out;
}

function muxOggOpus(opusHead: Uint8Array, packets: OpusPacket[]): Blob {
  // Deterministic serial (no Math.random needed for correctness; uniqueness
  // within one file is all Ogg requires).
  const serial = (oggCrc(opusHead) ^ 0x52494646) >>> 0;
  const pages: Uint8Array[] = [];
  let seq = 0;
  pages.push(buildPage(0x02, 0, serial, seq++, lacing([opusHead.length]), opusHead)); // BOS: OpusHead
  const tags = buildOpusTags();
  pages.push(buildPage(0x00, 0, serial, seq++, lacing([tags.length]), tags)); // OpusTags

  let granule = 0;
  let i = 0;
  while (i < packets.length) {
    const pagePackets: OpusPacket[] = [];
    let segCount = 0;
    while (i < packets.length) {
      const segs = Math.floor(packets[i].data.length / 255) + 1;
      if (pagePackets.length > 0 && segCount + segs > 255) break;
      segCount += segs;
      pagePackets.push(packets[i]);
      i++;
    }
    granule += pagePackets.reduce((s, p) => s + p.samples, 0);
    const segTable = lacing(pagePackets.map((p) => p.data.length));
    const payload = concat(pagePackets.map((p) => p.data));
    const isLast = i >= packets.length;
    pages.push(buildPage(isLast ? 0x04 : 0x00, granule, serial, seq++, segTable, payload));
  }
  return new Blob(pages.map((p) => p.slice().buffer), { type: 'audio/ogg' });
}

function concat(arrs: Uint8Array[]): Uint8Array {
  let len = 0;
  for (const a of arrs) len += a.length;
  const out = new Uint8Array(len);
  let off = 0;
  for (const a of arrs) {
    out.set(a, off);
    off += a.length;
  }
  return out;
}
